import { forwardRef, useCallback, useMemo, type ForwardedRef } from "react";
import { createColumnHelper, Row } from "@tanstack/react-table";
import { Table, type InfiniteScrollProps } from "@/components/ui/Table";
import { formatDate } from "@/lib/time";
import { CardChip } from "@/features/payment/components/CardChip";
import { CardStatusIcon } from "@/features/payment/components/CardStatusIcon";
import { PatientMeta } from "@/features/patient";
import RequestCardViaSMSBlock from "@/features/patient/components/request-card-via-sms-block/RequestCardViaSMSBlock";
import { Button } from "@/components/ui/Button";
import { CreditCard, Inbox } from "lucide-react";
import { usePatientStore } from "@/features/patient/store/usePatientStore";
import { AnalyticsEvent, trackEvent } from "@/lib/analytics";
import { CardCollectionRow } from "../types";
import { EmptyState } from "@/components/command-palette/components/EmptyState";
import { IconBox } from "@/components/ui/IconBox";
import { useBooleanFeatureFlag } from "@/lib/feature-flags/FeatureFlags";
import { FEATURE_FLAG_KEYS } from "@/lib/feature-flags/flags";

const columnHelper = createColumnHelper<CardCollectionRow>();

interface Props extends InfiniteScrollProps {
  items: CardCollectionRow[];
  isLoading: boolean;
}

function CardCollectionTableInner(
  {
    items,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  }: Props,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const setSelectedPatientId = usePatientStore((s) => s.setSelectedPatientId);
  const useResponsiveColumns = useBooleanFeatureFlag(
    FEATURE_FLAG_KEYS.cardCollectionResponsiveColumns,
  );

  const handleRowClick = useCallback(
    (row: Row<CardCollectionRow>) => {
      trackEvent(AnalyticsEvent.ManualCardCapture, {
        patient_id: row.original.patient.patientId,
      });
      setSelectedPatientId(row.original.patient.patientId);
    },
    [setSelectedPatientId],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("time", {
        header: "Time",
        size: 102,
        meta: useResponsiveColumns ? { naturalWidth: true } : undefined,
        cell: (cell) => (
          <span className="text-[12px] font-medium text-[#6B7280]">
            {formatDate(cell.getValue(), "h:mm a")}
          </span>
        ),
      }),
      columnHelper.display({
        id: "patient",
        header: "Patient",
        minSize: 460,
        meta: useResponsiveColumns ? { naturalWidth: true } : { grow: true },
        cell: (cell) => (
          <div className="flex flex-wrap items-center gap-3">
            <CardStatusIcon
              hasCard={!!cell.row.original.patient.cardOnFile?.last4}
            />
            <div className="flex flex-col gap-0.5">
              <span className="text-[13px] font-semibold text-[#101828]">
                {cell.row.original.patient.firstName}{" "}
                {cell.row.original.patient.lastName}
              </span>
              <PatientMeta patient={cell.row.original.patient} />
            </div>
          </div>
        ),
      }),
      columnHelper.accessor("patient.cardOnFile", {
        header: "Card on File",
        size: 212,
        meta: useResponsiveColumns ? { naturalWidth: true } : undefined,
        cell: (cell) => (
          <CardChip
            type="details"
            cardInfo={cell.getValue() || null}
            className="gap-2"
            last4ClassName="text-[13px] text-[#667085] font-semibold"
          />
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        size: 294,
        meta: useResponsiveColumns ? { naturalWidth: true } : undefined,
        cell: (cell) =>
          !cell.row.original.patient.cardOnFile && (
            <div className="flex flex-wrap gap-1.5">
              <RequestCardViaSMSBlock
                className="h-[32px]"
                surface="HomeScreen"
                patient={cell.row.original.patient}
              />
              <Button
                size="lg"
                className="h-[32px] text-[#1D2939]"
                color="secondary"
                variant="outlined"
                icon={CreditCard}
                onClick={() => {
                  trackEvent(AnalyticsEvent.ManualCardCapture, {
                    patient_id: cell.row.original.patient.patientId,
                  });
                  setSelectedPatientId(cell.row.original.patient.patientId);
                }}
              >
                Enter
              </Button>
            </div>
          ),
      }),
    ],
    [setSelectedPatientId, useResponsiveColumns],
  );

  return (
    <Table
      ref={ref}
      data={items}
      columns={columns}
      isLoading={isLoading}
      isFetching={isFetching}
      rowClassName={(row) =>
        row.original.patient.cardOnFile
          ? ""
          : "shadow-[inset_4px_0px_0px_0px_#CE2C31]"
      }
      pagination={{
        type: "infinite",
        next: fetchNextPage,
        hasMore: hasNextPage,
        isFetchingNextPage,
      }}
      onRowClick={handleRowClick}
      testId="patient-queue-table"
      getRowTestId={(row) =>
        `patient-queue-row-${row.original.patient.patientId}`
      }
      emptyState={
        <EmptyState
          icon={<IconBox icon={Inbox} preset="disabled" />}
          title="No appointments for today"
          subtitle="Appointments scheduled for today will appear here."
        />
      }
    />
  );
}

export const CardCollectionTable = forwardRef(CardCollectionTableInner);
